// Support inbox data (0141 threads/messages, 0315 category/urgency).
//
// Plain RLS reads, like the SOS hooks: the view and the policies are the only
// scope — a hub coordinator gets their hub's tickets, HQ gets everything — so
// there is no client-side hub filter to drift from the server's.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useSyncExternalStore } from "react";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/lib/supabase";
import type { LastMessage, SupportTicketRow } from "./support.model";

const SUPPORT_KEY = ["support"] as const;
const TICKETS_KEY = [...SUPPORT_KEY, "tickets"] as const;
const messagesKey = (threadId: string) => [...SUPPORT_KEY, "messages", threadId] as const;
const PAID_KEY = [...SUPPORT_KEY, "paid-without-booking"] as const;

const TICKET_COLUMNS =
  "id, ref, hub_id, booking_id, status, assigned_staff_id, client_first_name, created_at, updated_at, resolved_at, withdrawn_at, category, urgent, urgent_reason, urgent_override, author, staff_id, staff_first_name, staff_role";

export interface SupportTickets {
  tickets: SupportTicketRow[];
  bookingRefById: Map<string, string>;
  /** Assigned staff, first name + surname initial, by staff id. */
  staffNameById: Map<string, string>;
  /** Newest message per thread — the preview line in the conversation list. */
  lastMessageByThread: Map<string, LastMessage>;
}

/** Polled every 15 s — a new urgent ticket must not wait for a refresh. */
export function useSupportTickets() {
  return useQuery({
    queryKey: TICKETS_KEY,
    queryFn: async (): Promise<SupportTickets> => {
      const { data, error } = await supabase
        .from("v_support_thread_staff")
        .select(TICKET_COLUMNS)
        .order("updated_at", { ascending: false })
        .limit(300);
      if (error) throw error;
      const tickets = (data ?? []) as unknown as SupportTicketRow[];

      // Refs only — the booking's client never enters this query (data wall).
      const bookingIds = [...new Set(tickets.flatMap((t) => (t.booking_id ? [t.booking_id] : [])))];
      const bookingRefById = new Map<string, string>();
      if (bookingIds.length > 0) {
        const { data: bookings, error: refsError } = await supabase
          .from("bookings")
          .select("id, ref")
          .in("id", bookingIds);
        if (refsError) throw refsError;
        for (const b of bookings ?? []) bookingRefById.set(b.id, b.ref);
      }

      const staffIds = [
        ...new Set(tickets.flatMap((t) => (t.assigned_staff_id ? [t.assigned_staff_id] : []))),
      ];
      const staffNameById = new Map<string, string>();
      if (staffIds.length > 0) {
        const { data: staff, error: staffError } = await supabase
          .from("staff")
          .select("id, full_name")
          .in("id", staffIds);
        if (staffError) throw staffError;
        for (const s of staff ?? []) staffNameById.set(s.id, s.full_name);
      }

      // The newest messages across every thread this person can read (RLS is the
      // scope), newest first; the first one seen for a thread is its last. Not
      // `.in(thread ids)` — 300 uuids would overrun the request URL.
      const lastMessageByThread = new Map<string, LastMessage>();
      const { data: recent, error: recentError } = await supabase
        .from("support_messages")
        .select("thread_id, body, created_at, sender")
        .order("created_at", { ascending: false })
        .limit(1500);
      if (recentError) throw recentError;
      for (const m of recent ?? []) {
        if (!lastMessageByThread.has(m.thread_id)) {
          lastMessageByThread.set(m.thread_id, {
            body: m.body,
            created_at: m.created_at,
            sender: m.sender as LastMessage["sender"],
          });
        }
      }
      return { tickets, bookingRefById, staffNameById, lastMessageByThread };
    },
    refetchInterval: 30_000,
  });
}

const SEEN_PREFIX = "bednbody.support.seen.";
const NO_SEEN: Readonly<Record<string, string>> = {};

/**
 * What this person has looked at, per conversation — drives the unread dots AND
 * the count on the nav. One store shared by every caller (the sidebar badge and
 * the page), so opening a conversation clears both at once; separate component
 * state would leave the badge stale.
 *
 * Kept in this browser on purpose: it is a convenience, not a record, and it
 * needs no table. The trade is that it does not follow them to another device,
 * and a cleared browser marks everything unread again. Other tabs stay in step
 * through the `storage` event.
 */
let seenCache: { key: string; value: Record<string, string> } | null = null;
const seenListeners = new Set<() => void>();

function readSeen(key: string): Record<string, string> {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as Record<string, string>) : {};
  } catch {
    return {};
  }
}

function currentSeen(key: string): Record<string, string> {
  if (seenCache?.key !== key) seenCache = { key, value: readSeen(key) };
  return seenCache.value;
}

function subscribeSeen(onChange: () => void) {
  seenListeners.add(onChange);
  const onStorage = (event: StorageEvent) => {
    if (event.key?.startsWith(SEEN_PREFIX)) {
      seenCache = null;
      onChange();
    }
  };
  window.addEventListener("storage", onStorage);
  return () => {
    seenListeners.delete(onChange);
    window.removeEventListener("storage", onStorage);
  };
}

export function useSeenThreads(userId: string | undefined) {
  const key = userId ? `${SEEN_PREFIX}${userId}` : null;
  const seen = useSyncExternalStore(
    subscribeSeen,
    () => (key ? currentSeen(key) : NO_SEEN),
    () => NO_SEEN,
  );

  const markSeen = useCallback(
    (id: string, at: string) => {
      if (!key) return;
      const current = currentSeen(key);
      if (current[id] !== undefined && current[id] >= at) return;
      const next = { ...current, [id]: at };
      seenCache = { key, value: next };
      try {
        window.localStorage.setItem(key, JSON.stringify(next));
      } catch {
        // Storage can be blocked; the dot just comes back after a reload.
      }
      for (const notify of seenListeners) notify();
    },
    [key],
  );

  return { seen, markSeen };
}

export interface SupportMessage {
  id: string;
  sender: "client" | "ai" | "staff";
  /** Who wrote it — needed to tell a staff thread's author from its responders. */
  sender_id: string | null;
  body: string;
  created_at: string;
}

/** The conversation, oldest first. Realtime keeps it fresh while the panel is open. */
export function useSupportMessages(threadId: string | null) {
  return useQuery({
    queryKey: messagesKey(threadId ?? "none"),
    enabled: threadId !== null,
    queryFn: async (): Promise<SupportMessage[]> => {
      const { data, error } = await supabase
        .from("support_messages")
        .select("id, sender, sender_id, body, created_at")
        .eq("thread_id", threadId as string)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as SupportMessage[];
    },
    // Realtime does the work; this is only the fallback.
    refetchInterval: 30_000,
  });
}

export interface PaidWithoutBookingRow {
  payment_id: string;
  amount: number;
  paid_at: string | null;
  booking_refs: string[] | null;
  cancelled_at: string | null;
  reason: "cancelled_no_credit" | "no_booking";
}

/** Admin and superadmin only — the view refuses everyone else (it shows amounts). */
export function usePaidWithoutBooking(enabled: boolean) {
  return useQuery({
    queryKey: PAID_KEY,
    enabled,
    queryFn: async (): Promise<PaidWithoutBookingRow[]> => {
      const { data, error } = await supabase
        .from("v_support_paid_without_booking")
        .select("payment_id, amount, paid_at, booking_refs, cancelled_at, reason")
        .order("paid_at", { ascending: false });
      if (error) throw error;
      return (data ?? []).map((row) => ({
        ...(row as unknown as PaidWithoutBookingRow),
        amount: Number(row.amount),
      }));
    },
    refetchInterval: 60_000,
  });
}

/**
 * Superadmin only (0317): hides a test item from the card, needs a note, and
 * is audited. The payment itself is untouched.
 */
export function useDismissPaidWithoutBooking() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ paymentId, note }: { paymentId: string; note: string }) => {
      const { error } = await supabase.rpc("dismiss_paid_without_booking", {
        p_payment_id: paymentId,
        p_note: note,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PAID_KEY }),
  });
}

/**
 * Claim, reply, resolve (0141) and urgency (0315). Every authority check is in
 * SQL: claim/resolve check the hub, the reply policy pins the sender to the
 * caller, and urgency is admin/superadmin only.
 */
export function useSupportActions(threadId: string) {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const refresh = () => queryClient.invalidateQueries({ queryKey: SUPPORT_KEY });

  const claim = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc("claim_support_thread", { p_thread_id: threadId });
      if (error) throw new Error(error.message);
    },
    onSuccess: refresh,
  });

  const resolve = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc("resolve_support_thread", { p_thread_id: threadId });
      if (error) throw new Error(error.message);
    },
    onSuccess: refresh,
  });

  // 0335: only a resolved thread reopens; the SQL logs it to audit_log.
  const reopen = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc("reopen_support_thread", { p_thread_id: threadId });
      if (error) throw new Error(error.message);
    },
    onSuccess: refresh,
  });

  const reply = useMutation({
    mutationFn: async (body: string) => {
      const userId = session?.user.id;
      if (!userId) throw new Error("Sign in again before replying.");
      const { error } = await supabase.from("support_messages").insert({
        thread_id: threadId,
        sender: "staff",
        sender_id: userId,
        body,
        client_request_id: crypto.randomUUID(),
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: refresh,
  });

  const setUrgency = useMutation({
    // null = back to the automatic rules.
    mutationFn: async (urgent: boolean | null) => {
      const { error } = await supabase.rpc("set_support_ticket_urgency", {
        p_thread_id: threadId,
        p_urgent: urgent as boolean,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: refresh,
  });

  return { claim, resolve, reopen, reply, setUrgency };
}
